"use client";

import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { UrlFilterState } from "@/hooks/use-url-filters";
import type { ErrorClassification } from "@/types/logs";
import { formatUtcFilterInstant } from "@/utils/date-utils";

interface Chip {
  label: string;
  onRemove?: () => void;
}

interface ActiveFilterChipsProps {
  filters: UrlFilterState;
  onRemoveUser: (userId: string) => void;
  onRemoveRoute: (route: string) => void;
  onRemoveMethod: (method: string) => void;
  onRemoveErrorStatus: (status: ErrorClassification) => void;
  onRemoveStatusCode: (statusCode: number) => void;
  onRemoveResponseStored: () => void;
  onRemoveFixedStatus: () => void;
  onRemoveCreatedRange: () => void;
  onRemoveUpdatedRange: () => void;
  onRemoveSearch: () => void;
  onRemoveResponseTimeRange?: () => void;
  /** Routes that cannot be dismissed via chips (e.g. locked on Route Details). */
  lockedRoutes?: string[];
}

export function ActiveFilterChips({
  filters,
  onRemoveUser,
  onRemoveRoute,
  onRemoveMethod,
  onRemoveErrorStatus,
  onRemoveStatusCode,
  onRemoveResponseStored,
  onRemoveFixedStatus,
  onRemoveCreatedRange,
  onRemoveUpdatedRange,
  onRemoveSearch,
  onRemoveResponseTimeRange,
  lockedRoutes = [],
}: ActiveFilterChipsProps) {
  const locked = new Set(lockedRoutes);
  const chips: Chip[] = [
    ...lockedRoutes
      .filter((route) => filters.routes?.includes(route))
      .map((route) => ({ label: `Route: ${route}` })),
    ...(filters.userIds?.map((v) => ({ label: `User: ${v}`, onRemove: () => onRemoveUser(v) })) ?? []),
    ...(filters.routes
      ?.filter((v) => !locked.has(v))
      .map((v) => ({ label: `Route: ${v}`, onRemove: () => onRemoveRoute(v) })) ?? []),
    ...(filters.methods?.map((v) => ({ label: `Method: ${v}`, onRemove: () => onRemoveMethod(v) })) ?? []),
    ...(filters.errorStatuses?.map((v) => ({ label: `Status: ${v}`, onRemove: () => onRemoveErrorStatus(v) })) ?? []),
    ...(filters.statusCodes?.map((v) => ({ label: `Code: ${v}`, onRemove: () => onRemoveStatusCode(v) })) ?? []),
  ];

  if (filters.responseStored && filters.responseStored !== "all") {
    chips.push({
      label: `Response ${filters.responseStored === "stored" ? "stored" : "not stored"}`,
      onRemove: onRemoveResponseStored,
    });
  }
  if (filters.fixedStatus && filters.fixedStatus !== "all") {
    chips.push({
      label: filters.fixedStatus === "fixed" ? "Fixed only" : "Not fixed",
      onRemove: onRemoveFixedStatus,
    });
  }
  if (filters.createdFrom || filters.createdTo) {
    const from = filters.createdFrom ? formatUtcFilterInstant(filters.createdFrom) : "…";
    const to = filters.createdTo ? formatUtcFilterInstant(filters.createdTo) : "…";
    chips.push({ label: `Created ${from} – ${to}`, onRemove: onRemoveCreatedRange });
  }
  if (filters.updatedFrom || filters.updatedTo) {
    const from = filters.updatedFrom ? formatUtcFilterInstant(filters.updatedFrom) : "…";
    const to = filters.updatedTo ? formatUtcFilterInstant(filters.updatedTo) : "…";
    chips.push({ label: `Updated ${from} – ${to}`, onRemove: onRemoveUpdatedRange });
  }
  if (
    onRemoveResponseTimeRange &&
    (filters.responseTimeMinMs !== undefined || filters.responseTimeMaxMs !== undefined)
  ) {
    const min = filters.responseTimeMinMs ?? 0;
    const max = filters.responseTimeMaxMs ?? "∞";
    chips.push({
      label: `Response time ${min}–${max} ms`,
      onRemove: onRemoveResponseTimeRange,
    });
  }
  if (filters.search) {
    chips.push({ label: `Search: "${filters.search}"`, onRemove: onRemoveSearch });
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5" role="list" aria-label="Active filters">
      {chips.map((chip, index) => (
        <Badge
          key={`${chip.label}-${index}`}
          variant="secondary"
          className={`gap-1 text-xs ${chip.onRemove ? "pr-1" : ""}`}
          role="listitem"
        >
          {chip.label}
          {chip.onRemove ? (
            <button
              type="button"
              onClick={chip.onRemove}
              className="rounded-full p-0.5 hover:bg-background/60"
              aria-label={`Remove filter ${chip.label}`}
            >
              <X className="size-3" aria-hidden="true" />
            </button>
          ) : null}
        </Badge>
      ))}
    </div>
  );
}
